/* eslint-disable import/prefer-default-export */

import { useCallback, useRef } from 'react';
import usePortal from 'react-useportal';
import styled from 'styled-components';

const ModalDiv = styled.div<{ showHeader?: boolean }>`
  z-index: ${(props) => (props.showHeader ? 5 : 15)};
`;

type UsePortalEvent = Parameters<Parameters<typeof usePortal>[0]['onOpen']>[0];

type PropsType = {
  onOpen?: (event?: UsePortalEvent) => void;
  onClose?: (event?: UsePortalEvent) => void;
  closeOnOutsideClick?: boolean;
  closeOnEsc?: boolean;
  background?: string;
  fullscreen?: boolean;
  showHeader?: boolean;
  zIndexOffset?: number;
  programmaticallyOpen?: boolean;
};

export const useModal = ({
  onOpen,
  onClose,
  closeOnOutsideClick = true,
  closeOnEsc = true,
  background = '',
  fullscreen = false,
  showHeader = false,
  zIndexOffset = 0,
  programmaticallyOpen = false,
}: PropsType) => {
  const modalStyle = `
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%,-50%);
    z-index: ${(showHeader ? 5 : 15) + zIndexOffset};
  `;

  const backgroundStyle = `
    position: fixed;
    display: ${fullscreen ? 'block' : 'flex'};
    justify-content: center;
    align-items: center;
    background-color: ${background ?? 'transparent'};
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: ${(showHeader ? 5 : 15) + zIndexOffset};
  `;

  const modal = useRef(null);

  const { isOpen, togglePortal, openPortal, closePortal, Portal } = usePortal({
    onOpen: (event) => {
      const { portal } = event;

      // 'react-useportal' library's suggested way to change styling
      portal.current.style.cssText = background === '' ? modalStyle : backgroundStyle;

      if (onOpen) {
        onOpen(event);
      }
    },
    onClose: (event) => {
      const { portal } = event;

      // 'react-useportal' library's suggested way to change styling
      portal.current.removeAttribute('style');

      if (onClose) {
        onClose(event);
      }
    },
    onPortalClick: (event) => {
      const clickingOutsideModal = modal && modal.current && !modal.current.contains(event.target);
      if (clickingOutsideModal && closeOnOutsideClick) {
        closePortal(event);
      }
    },
    closeOnOutsideClick,
    closeOnEsc,
    bindTo: document.getElementById('app-modal') ?? undefined,
    programmaticallyOpen,
  });

  const ModalWithBackground = useCallback(
    (props) => (
      <Portal>
        <ModalDiv ref={modal} role="dialog" {...props} showHeader />
      </Portal>
    ),
    [Portal]
  );

  const Modal = background ? ModalWithBackground : Portal;

  return {
    Modal,
    toggleModal: togglePortal,
    openModal: openPortal,
    closeModal: closePortal,
    isOpen,
    modalRef: modal,
  };
};
